import { jsPDF } from 'jspdf';
import { getResutarantBillHTMLString } from '@src/app/components/Print';
import { getRoomPdfHTMLString, getRoomPdfHTMLStrings } from '@src/app/components/Print/PrintRoomPDF';

/** html2canvas + jsPDF html() use shared DOM iframes — render one PDF at a time. */
export const BILL_PDF_CONCURRENCY = 1;

const PAGE = {
    widthPx: Math.round(210 * (96 / 25.4)),
    heightPx: Math.round(297 * (96 / 25.4)),
};

const IMAGE_LOAD_TIMEOUT_MS = 5000;

export async function resolveBillHtml(
    order: Record<string, unknown>,
    restaurant: Record<string, unknown>,
    isRoomReport: boolean
): Promise<string> {
    if (!isRoomReport) {
        return getResutarantBillHTMLString(order, restaurant);
    }
    return restaurant?.esign
        ? getRoomPdfHTMLStrings(order, restaurant)
        : getRoomPdfHTMLString(order, restaurant);
}

/** Warm browser cache for logos/signatures reused across every bill in a batch. */
export async function preloadBillImages(restaurant: Record<string, unknown>): Promise<void> {
    const urls = [
        restaurant?.logo,
        restaurant?.managerSign,
        restaurant?.managerSignFoodBill,
    ].filter((url): url is string => typeof url === 'string' && url.length > 0);

    await Promise.all(
        urls.map(
            (url) =>
                new Promise<void>((resolve) => {
                    const img = new Image();
                    const finish = () => resolve();
                    img.onload = finish;
                    img.onerror = finish;
                    setTimeout(finish, IMAGE_LOAD_TIMEOUT_MS);
                    img.src = url;
                })
        )
    );
}

export async function generateBillPdfBlob(
    htmlContent: string,
    meta: { title: string; author: string },
    isRoomReport: boolean
): Promise<Blob> {
    const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: [PAGE.widthPx, PAGE.heightPx],
    });

    pdf.setProperties({
        title: meta.title,
        subject: meta.title,
        author: meta.author,
        creator: 'Khata by iitnirvana',
    });

    const margin = isRoomReport ? 64 : 10;
    const contentWidth = isRoomReport ? PAGE.widthPx - 128 : 132;
    const windowWidth = isRoomReport ? PAGE.widthPx - 128 : 400;

    // Pass HTML string — jsPDF creates its own render container.
    // Hidden/off-screen DOM nodes produce blank captures in html2canvas.
    await pdf.html(htmlContent, {
        x: 0,
        y: 0,
        margin,
        width: contentWidth,
        windowWidth,
        html2canvas: {
            scale: 1,
        },
    });

    return pdf.output('blob');
}

export async function runWithConcurrency<T>(
    items: T[],
    concurrency: number,
    worker: (item: T, index: number) => Promise<void>
): Promise<void> {
    if (!items.length) {
        return;
    }

    let cursor = 0;
    const poolSize = Math.min(Math.max(1, concurrency), items.length);

    async function runSlot() {
        while (cursor < items.length) {
            const index = cursor;
            cursor += 1;
            await worker(items[index], index);
        }
    }

    await Promise.all(Array.from({ length: poolSize }, runSlot));
}

export function allocateUniquePdfFileName(baseName: string, usedNames: Map<string, number>): string {
    const count = usedNames.get(baseName) ?? 0;
    usedNames.set(baseName, count + 1);
    if (count === 0) {
        return `${baseName}.pdf`;
    }
    return `${baseName}-${count + 1}.pdf`;
}
